import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, ApiError } from "./api";

/* AuthContext — the source of truth for "who's logged in".

   Lifecycle:
   1. On mount, call /auth/me. If 200, set the user. If 401, the api
      client already tried a refresh; final failure = not logged in.
   2. Provide login(), logout(), refreshUser() for the auth flows.
   3. Expose `loading` so the route guards can show a spinner on
      initial hydrate instead of flashing to /login.

   The user shape matches the API:
     { id, email, name, role, active, lastLoginAt, createdAt } */

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      /* Shorter than the 45s default: every other request happens while
         the app is already usable, but this one blocks the FIRST render --
         nothing shows until it settles. Ten seconds is long enough for a
         slow connection or a waking Neon compute, and short enough that
         the login screen appears before anyone force-closes the app. */
      const data = await api.get("/auth/me", { timeoutMs: 10_000 });
      setUser(data?.user || null);
    } catch (err) {
      // 401 / no session — not an error, just not logged in.
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Real error (network, 5xx) — still treat as unauthenticated so
        // route guard kicks user to login. They'll see the issue then.
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const data = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Even if the server call fails, clear local state — user
      // initiated logout, respect that.
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/* Role-aware URL prefix helper. Returns the part of the URL that
   reflects the user's "scope". Brand-prefixed for super_admin / admin
   so the URLs are intentional, not generic.
     super_admin → /abhisuperadmin
     admin       → /abhiadmin
     employee    → /employee */
export function rolePrefix(role) {
  if (role === "super_admin") return "/abhisuperadmin";
  if (role === "admin") return "/abhiadmin";
  if (role === "employee") return "/employee";
  return "/portal-x7k3";
}
